/**
 * MessageNew工具类 - 聊天消息处理工具（按照接口文档更新）
 * @Author: Christion.Young
 * @Date: 2025-09-18 10:00:00
 * @Description: 基于接口文档实现的统一消息处理工具，支持消息发送、格式化和状态管理
 */

import API from "@/api";
import store from "@/store";

/**
 * 统一消息发送工具函数 - 按照接口文档实现
 * @param {Object} val - 消息参数对象
 * @param {string} val.toUserId - 必需：接收方用户ID
 * @param {string} val.toUserName - 必需：接收方用户名
 * @param {string} val.message - 可选：消息内容（聊天消息和系统通知时需要）
 * @param {string} val.state - 必需：消息状态类型
 * @param {string} val.page - 必需：来源页面路径
 * @param {string} val.orderId - 可选：关联订单ID（订单相关消息时需要）
 * @param {Object} subscribe - 可选：微信订阅消息参数
 * @returns {Promise<void>}
 */
export const MessageNew = async (val, subscribe) => {
  // 获取当前用户信息
  const userInfo = store.getters["user/userInfo"] || {};
  const userStore = {
    userId: userInfo.id || userInfo.userId,
    userInfo: {
      nickname: userInfo.nickname || userInfo.username || "用户",
    },
  };

  // 构造基础消息对象
  let messageObj = {
    fromUserId: userStore.userId,
    fromUserName: userStore.userInfo.nickname,
    toUserId: val.toUserId,
    toUserName: val.toUserName,
    content: "",
    msgType: "",
    orderId: "",
    page: val.page,
    readStatus: "0", // 默认未读
  };

  // 根据消息状态设置内容和类型
  switch (val.state) {
    case "聊天消息":
      messageObj.content = val.message;
      messageObj.msgType = "text";
      break;
    case "下单成功":
      messageObj.content = "您有新的订单，请及时处理";
      messageObj.msgType = "order";
      messageObj.orderId = val.orderId;
      break;
    case "接单成功":
      messageObj.content = "商家已接单，请耐心等待";
      messageObj.msgType = "order";
      messageObj.orderId = val.orderId;
      break;
    case "订单完成":
      messageObj.content = "订单已完成，请对服务进行评价";
      messageObj.msgType = "order";
      messageObj.orderId = val.orderId;
      break;
    case "订单取消":
      messageObj.content = "订单已取消";
      messageObj.msgType = "order";
      messageObj.orderId = val.orderId;
      break;
    case "系统通知":
      messageObj.content = val.message;
      messageObj.msgType = "system";
      break;
    default:
      messageObj.content = val.message || "新消息";
      messageObj.msgType = "text";
      break;
  }

  try {
    // 调用消息添加接口
    const response = await API.message.messageAdd(messageObj);
    
    if (response && response.code === 0) {
      console.log("消息发送成功:", response.data?.id);
      
      // 如果有订阅消息参数，发送微信订阅消息
      if (subscribe) {
        try {
          await sendSubscriptionMsg(subscribe);
        } catch (error) {
          console.error("发送订阅消息失败:", error);
        }
      }
      
      return response.data;
    } else {
      throw new Error(response?.msg || "发送消息失败");
    }
  } catch (error) {
    console.error("发送消息失败:", error);
    throw error;
  }
};

/**
 * 发送微信订阅消息（暂时模拟实现）
 * @param {Object} subscribeData - 订阅消息数据
 * @returns {Promise<void>}
 */
const sendSubscriptionMsg = async (subscribeData) => {
  // 这里应该调用实际的微信订阅消息接口
  // 暂时只是日志记录
  console.log("发送微信订阅消息:", subscribeData);
  return Promise.resolve();
};

/**
 * MessageNew 工具类
 */
class MessageNewClass {
  /**
   * 创建新消息对象
   * @param {Object} options - 消息选项
   * @param {string} options.content - 消息内容
   * @param {string} options.employeeId - 员工ID
   * @param {string} options.createId - 创建者ID
   * @param {string} options.type - 消息类型 ('text', 'image', 'file' 等)
   * @returns {Object} 格式化的消息对象
   */
  static create(options = {}) {
    const {
      content = "",
      employeeId = "",
      createId = "",
      type = "text",
    } = options;

    return {
      content,
      employeeId,
      createId,
      type,
      timestamp: Date.now(),
      createTime: new Date().toISOString(),
    };
  }

  /**
   * 验证消息内容
   * @param {string} content - 消息内容
   * @returns {Object} 验证结果
   */
  static validateContent(content) {
    const result = {
      valid: true,
      errors: [],
    };

    // 检查是否为空
    if (!content || !content.trim()) {
      result.valid = false;
      result.errors.push("消息内容不能为空");
      return result;
    }

    // 检查长度
    if (content.length > 500) {
      result.valid = false;
      result.errors.push("消息内容过长，请控制在500字以内");
    }

    // 检查敏感词（可以根据需要扩展）
    const sensitiveWords = ["测试敏感词"]; // 实际项目中应该从配置或API获取
    const hasSensitiveWord = sensitiveWords.some((word) =>
      content.toLowerCase().includes(word.toLowerCase())
    );

    if (hasSensitiveWord) {
      result.valid = false;
      result.errors.push("消息包含敏感词，请修改后重试");
    }

    return result;
  }

  /**
   * 格式化时间显示
   * @param {number|string|Date} timestamp - 时间戳或日期
   * @returns {string} 格式化的时间字符串
   */
  static formatTime(timestamp) {
    if (!timestamp) {
      return "";
    }

    try {
      const now = new Date();
      const msgTime = new Date(timestamp);
      const diffTime = now.getTime() - msgTime.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        // 今天，显示时间
        return msgTime.toLocaleTimeString("zh-CN", {
          hour: "2-digit",
          minute: "2-digit",
        });
      } else if (diffDays === 1) {
        // 昨天
        return "昨天";
      } else if (diffDays < 7) {
        // 一周内，显示星期
        const weekdays = ["日", "一", "二", "三", "四", "五", "六"];
        return `周${weekdays[msgTime.getDay()]}`;
      } else {
        // 超过一周，显示日期
        return msgTime.toLocaleDateString("zh-CN", {
          month: "2-digit",
          day: "2-digit",
        });
      }
    } catch (error) {
      console.error("时间格式化失败:", error);
      return timestamp;
    }
  }

  /**
   * 处理消息内容（转义HTML、处理换行等）
   * @param {string} content - 原始消息内容
   * @returns {string} 处理后的消息内容
   */
  static processContent(content) {
    if (!content) return "";

    // HTML转义
    const htmlEscaped = content
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

    // 处理换行
    const withBreaks = htmlEscaped.replace(/\n/g, "<br>");

    // 处理URL链接（可选功能）
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const withLinks = withBreaks.replace(
      urlRegex,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    return withLinks;
  }

  /**
   * 转换API消息格式为组件格式
   * @param {Array} apiMessages - API返回的消息数组
   * @param {string} currentUserId - 当前用户ID
   * @returns {Array} 转换后的消息数组
   */
  static convertApiToComponent(apiMessages, currentUserId) {
    if (!Array.isArray(apiMessages)) {
      return [];
    }

    return apiMessages.map((msg) => ({
      id: msg.id,
      type: msg.fromUserId === currentUserId ? "user" : "service",
      content: msg.content,
      timestamp: new Date(msg.createTime).getTime(),
      createTime: msg.createTime,
      fromUserId: msg.fromUserId,
      toUserId: msg.toUserId,
      msgType: msg.msgType,
      readStatus: msg.readStatus,
      isRevoked: msg.isRevoked,
    }));
  }

  /**
   * 生成聊天会话的唯一标识
   * @param {string} userId - 用户ID
   * @param {string} employeeId - 员工ID
   * @returns {string} 会话标识
   */
  static generateChatId(userId, employeeId) {
    return `chat_${userId}_${employeeId}`;
  }

  /**
   * 获取消息发送者显示名称
   * @param {Object} message - 消息对象
   * @param {string} currentUserId - 当前用户ID
   * @param {string} employeeName - 员工名称
   * @returns {string} 显示名称
   */
  static getSenderName(message, currentUserId, employeeName = "客服") {
    if (message.fromUserId === currentUserId) {
      return "我";
    }
    return employeeName;
  }

  /**
   * 检查是否需要显示时间分隔线
   * @param {Object} currentMessage - 当前消息
   * @param {Object} previousMessage - 上一条消息
   * @param {number} interval - 时间间隔（分钟）
   * @returns {boolean} 是否需要显示时间
   */
  static shouldShowTime(currentMessage, previousMessage, interval = 5) {
    if (!previousMessage) return true;

    const currentTime = new Date(currentMessage.timestamp);
    const previousTime = new Date(previousMessage.timestamp);
    const diff = (currentTime - previousTime) / (1000 * 60); // 转换为分钟

    return diff >= interval;
  }

  /**
   * 合并连续的相同发送者消息
   * @param {Array} messages - 消息数组
   * @returns {Array} 处理后的消息数组
   */
  static groupConsecutiveMessages(messages) {
    if (!Array.isArray(messages) || messages.length === 0) {
      return [];
    }

    const grouped = [];
    let currentGroup = null;

    messages.forEach((message) => {
      if (!currentGroup || currentGroup.fromUserId !== message.fromUserId) {
        // 开始新的分组
        currentGroup = {
          ...message,
          messages: [message],
        };
        grouped.push(currentGroup);
      } else {
        // 添加到当前分组
        currentGroup.messages.push(message);
        // 更新分组的时间戳为最新消息的时间
        currentGroup.timestamp = message.timestamp;
      }
    });

    return grouped;
  }

  /**
   * 标记消息为已读
   * @param {string} messageId - 消息ID
   * @returns {Promise<void>}
   */
  static async markMessageAsRead(messageId) {
    try {
      const response = await API.message.messageAmend({
        id: messageId,
        readStatus: "1",
      });
      
      if (response && response.code === 0) {
        console.log("消息已标记为已读:", messageId);
        return response.data;
      } else {
        throw new Error(response?.msg || "标记消息已读失败");
      }
    } catch (error) {
      console.error("标记消息已读失败:", error);
      throw error;
    }
  }

  /**
   * 撤回消息
   * @param {string} messageId - 消息ID
   * @returns {Promise<void>}
   */
  static async revokeMessage(messageId) {
    try {
      const response = await API.message.messageAmend({
        id: messageId,
        isRevoked: true,
        content: "[消息已撤回]",
      });
      
      if (response && response.code === 0) {
        console.log("消息已撤回:", messageId);
        return response.data;
      } else {
        throw new Error(response?.msg || "撤回消息失败");
      }
    } catch (error) {
      console.error("撤回消息失败:", error);
      throw error;
    }
  }
}

/**
 * 消息类型图标映射
 */
export const MESSAGE_TYPE_ICONS = {
  text: "el-icon-chat-dot-round",
  image: "el-icon-picture",
  order: "el-icon-s-order",
  system: "el-icon-warning",
};

/**
 * 获取消息类型显示文本
 * @param {string} msgType - 消息类型
 * @returns {string} 显示文本
 */
export const getMessageTypeText = (msgType) => {
  const typeMap = {
    text: "文本消息",
    image: "图片消息",
    order: "订单消息",
    system: "系统消息",
  };
  return typeMap[msgType] || "未知消息";
};

// 导出默认类和新增的函数
export default MessageNewClass;
